import { useReducer, useCallback, useRef, useEffect } from 'react'
import api from '@/lib/api'
import type { BuilderState, BuilderAction, WizardResponses } from './types'

const initialState: BuilderState = {
  phase: 'url',
  completedPhases: new Set(),
  websiteUrl: '',
  scrapeStatus: 'idle',
  businessName: '',
  practiceAreas: [],
  businessDescription: '',
  contactInfo: {},
  wizardResponses: {
    uniqueSellingPoints: '',
    typicalClientConcerns: '',
    consultationProcess: '',
    feeStructure: '',
    urgentCaseHandling: '',
  },
  selectedVoiceId: 'molly',
  tonePreset: 'balanced',
  agentName: '',
  greeting: '',
  greetingManuallyEdited: false,
  // Operations defaults
  firmTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  businessHours: 'Monday through Friday, 9am to 5pm',
  officePhone: '',
  primaryLanguage: 'English',
  additionalLanguages: [],
  afterHoursMessage: '',
  bookingUrl: '',

  knowledgeItems: [],
  selectedPhoneId: '',
  voiceOptions: [],
  phoneNumbers: [],
  voicesLoading: false,
  creating: false,
  error: null,
}

function reducer(state: BuilderState, action: BuilderAction): BuilderState {
  switch (action.type) {
    case 'SET_PHASE':
      return { ...state, phase: action.phase }
    case 'COMPLETE_PHASE': {
      const next = new Set(state.completedPhases)
      next.add(action.phase)
      return { ...state, completedPhases: next }
    }
    case 'SET_URL':
      return { ...state, websiteUrl: action.url }
    case 'SET_SCRAPE_STATUS':
      return { ...state, scrapeStatus: action.status }
    case 'SET_SCRAPED_DATA':
      return {
        ...state,
        businessName: action.data.businessName || state.businessName,
        practiceAreas: action.data.practiceAreas?.length ? action.data.practiceAreas : state.practiceAreas,
        businessDescription: action.data.description || state.businessDescription,
        contactInfo: action.data.contactInfo || state.contactInfo,
      }
    case 'SET_BUSINESS_NAME':
      return { ...state, businessName: action.name }
    case 'SET_PRACTICE_AREAS':
      return { ...state, practiceAreas: action.areas }
    case 'SET_DESCRIPTION':
      return { ...state, businessDescription: action.description }
    case 'SET_WIZARD_RESPONSE':
      return { ...state, wizardResponses: { ...state.wizardResponses, [action.field]: action.value } }
    case 'PREFILL_WIZARD': {
      // Only fill fields that are still empty — anything the user already
      // typed wins, even if the prefill API call returned later.
      const next = { ...state.wizardResponses }
      for (const [k, v] of Object.entries(action.suggestions)) {
        const key = k as keyof WizardResponses
        if (typeof v === 'string' && v.trim() && !next[key]?.trim()) {
          next[key] = v.trim()
        }
      }
      return { ...state, wizardResponses: next }
    }
    case 'SET_VOICE':
      return { ...state, selectedVoiceId: action.voiceId }
    case 'SET_TONE':
      return { ...state, tonePreset: action.tone }
    case 'SET_AGENT_NAME':
      return { ...state, agentName: action.name }
    case 'SET_GREETING':
      return {
        ...state,
        greeting: action.greeting,
        greetingManuallyEdited: action.manual ?? state.greetingManuallyEdited,
      }
    case 'SET_PHONE':
      return { ...state, selectedPhoneId: action.phoneId }
    case 'SET_VOICE_OPTIONS':
      return { ...state, voiceOptions: action.voices }
    case 'SET_PHONE_NUMBERS':
      return { ...state, phoneNumbers: action.numbers }
    case 'SET_VOICES_LOADING':
      return { ...state, voicesLoading: action.loading }
    case 'SET_CREATING':
      return { ...state, creating: action.creating }
    case 'SET_ERROR':
      return { ...state, error: action.error }
    case 'SET_FIRM_TIMEZONE':
      return { ...state, firmTimezone: action.timezone }
    case 'SET_BUSINESS_HOURS':
      return { ...state, businessHours: action.hours }
    case 'SET_OFFICE_PHONE':
      return { ...state, officePhone: action.phone }
    case 'SET_PRIMARY_LANGUAGE':
      return { ...state, primaryLanguage: action.language }
    case 'SET_ADDITIONAL_LANGUAGES':
      return { ...state, additionalLanguages: action.languages }
    case 'SET_AFTER_HOURS_MESSAGE':
      return { ...state, afterHoursMessage: action.message }
    case 'SET_BOOKING_URL':
      return { ...state, bookingUrl: action.url }
    case 'SET_KNOWLEDGE_ITEMS':
      return { ...state, knowledgeItems: action.items }
    case 'ADD_KNOWLEDGE_ITEM':
      return { ...state, knowledgeItems: [...state.knowledgeItems, action.item] }
    case 'REMOVE_KNOWLEDGE_ITEM':
      return { ...state, knowledgeItems: state.knowledgeItems.filter(i => i.id !== action.id) }
    case 'RESET':
      return { ...initialState, completedPhases: new Set() }
    default:
      return state
  }
}

export function useAgentBuilder(open: boolean, mode: 'create' | 'configure' = 'create') {
  const [state, dispatch] = useReducer(reducer, initialState)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Reset when sheet closes
  useEffect(() => {
    if (!open) {
      dispatch({ type: 'RESET' })
      if (pollRef.current) clearInterval(pollRef.current)
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
    }
  }, [open])

  // Load voices and phone numbers on open
  useEffect(() => {
    if (!open) return
    const load = async () => {
      dispatch({ type: 'SET_VOICES_LOADING', loading: true })
      try {
        const [voicesRes, numbersRes] = await Promise.all([
          api.get('/api/voice/approved-voices'),
          api.get('/api/voice/numbers'),
        ])
        if (voicesRes.data?.success) {
          const mapped = (voicesRes.data.voices || []).map((v: any) => ({
            voice_id: v.id,
            name: v.name,
            gender: v.gender,
          }))
          dispatch({ type: 'SET_VOICE_OPTIONS', voices: mapped })
        }
        if (numbersRes.data?.success) {
          const unassigned = (numbersRes.data.phoneNumbers || []).filter(
            (p: any) => !p.assistantId && p.status === 'active'
          )
          dispatch({ type: 'SET_PHONE_NUMBERS', numbers: unassigned })
        }
      } catch {
        // Non-fatal — voices/numbers can be loaded later
      } finally {
        dispatch({ type: 'SET_VOICES_LOADING', loading: false })
      }
    }
    load()
  }, [open])

  // Load existing data on open
  useEffect(() => {
    if (!open) return

    // Check for existing scraped data
    api.get('/api/onboarding/scraped-data').then(res => {
      if (res.data?.status === 'complete' && res.data.data) {
        const d = res.data.data
        dispatch({
          type: 'SET_SCRAPED_DATA',
          data: {
            businessName: d.businessName,
            practiceAreas: d.practiceAreas,
            description: d.description,
            contactInfo: d.contactInfo,
          },
        })
        if (d.baseUrl) dispatch({ type: 'SET_URL', url: d.baseUrl })
        dispatch({ type: 'SET_SCRAPE_STATUS', status: 'preloaded' })

        // Pre-fill the agent-builder wizard from AI-drafted starter answers.
        // PREFILL_WIZARD only writes fields that are still empty, so anything
        // the user has already typed wins. Skipped in configure mode — the
        // voice-config load below restores saved answers verbatim.
        if (mode !== 'configure' && d.aiSuggestedWizardAnswers) {
          dispatch({ type: 'PREFILL_WIZARD', suggestions: d.aiSuggestedWizardAnswers })
        }
      }
    }).catch(() => {})

    // In configure mode, also load existing voice config to pre-populate all fields
    if (mode === 'configure') {
      api.get('/api/voice/config').then(res => {
        if (res.data?.success && res.data.config) {
          const c = res.data.config
          if (c.greetingName) dispatch({ type: 'SET_BUSINESS_NAME', name: c.greetingName })
          if (c.practiceAreas?.length) dispatch({ type: 'SET_PRACTICE_AREAS', areas: c.practiceAreas })
          if (c.voiceId) dispatch({ type: 'SET_VOICE', voiceId: c.voiceId })
          if (c.tonePreset) dispatch({ type: 'SET_TONE', tone: c.tonePreset })
          if (c.agentName) dispatch({ type: 'SET_AGENT_NAME', name: c.agentName })
          if (c.wizardResponses) {
            for (const [key, value] of Object.entries(c.wizardResponses)) {
              if (value) dispatch({ type: 'SET_WIZARD_RESPONSE', field: key as any, value: value as string })
            }
          }
          // Load operational fields
          if (c.firmTimezone) dispatch({ type: 'SET_FIRM_TIMEZONE', timezone: c.firmTimezone })
          if (c.businessHours) dispatch({ type: 'SET_BUSINESS_HOURS', hours: c.businessHours })
          if (c.officePhone) dispatch({ type: 'SET_OFFICE_PHONE', phone: c.officePhone })
          if (c.primaryLanguage) dispatch({ type: 'SET_PRIMARY_LANGUAGE', language: c.primaryLanguage })
          if (c.additionalLanguages?.length) dispatch({ type: 'SET_ADDITIONAL_LANGUAGES', languages: c.additionalLanguages })
          if (c.afterHoursMessage) dispatch({ type: 'SET_AFTER_HOURS_MESSAGE', message: c.afterHoursMessage })
          if (c.bookingUrl) dispatch({ type: 'SET_BOOKING_URL', url: c.bookingUrl })
          // Mark all phases as completed so user can jump to any section
          dispatch({ type: 'COMPLETE_PHASE', phase: 'url' })
          dispatch({ type: 'COMPLETE_PHASE', phase: 'discovery' })
          dispatch({ type: 'COMPLETE_PHASE', phase: 'questions' })
          dispatch({ type: 'COMPLETE_PHASE', phase: 'operations' })
          // Start on discovery phase so they see their current data
          dispatch({ type: 'SET_PHASE', phase: 'discovery' })
        }
      }).catch(() => {})
    }
  }, [open, mode])

  const startScrape = useCallback(async (url: string) => {
    dispatch({ type: 'SET_URL', url })
    dispatch({ type: 'SET_SCRAPE_STATUS', status: 'scraping' })
    dispatch({ type: 'SET_ERROR', error: null })

    try {
      await api.post('/api/onboarding/rescrape', { websiteUrl: url })

      // Poll for results
      let attempts = 0
      pollRef.current = setInterval(async () => {
        attempts++
        if (attempts > 20) {
          if (pollRef.current) clearInterval(pollRef.current)
          dispatch({ type: 'SET_SCRAPE_STATUS', status: 'failed' })
          dispatch({ type: 'SET_ERROR', error: 'Website analysis timed out. You can enter details manually.' })
          return
        }
        try {
          const res = await api.get('/api/onboarding/scraped-data')
          if (res.data?.status === 'complete' && res.data.data) {
            if (pollRef.current) clearInterval(pollRef.current)
            const d = res.data.data
            dispatch({
              type: 'SET_SCRAPED_DATA',
              data: {
                businessName: d.businessName,
                practiceAreas: d.practiceAreas,
                description: d.description,
                contactInfo: d.contactInfo,
              },
            })
            // Apply AI-drafted wizard answers to any fields the user
            // hasn't filled in yet. PREFILL_WIZARD only writes blanks.
            if (mode !== 'configure' && d.aiSuggestedWizardAnswers) {
              dispatch({ type: 'PREFILL_WIZARD', suggestions: d.aiSuggestedWizardAnswers })
            }
            dispatch({ type: 'SET_SCRAPE_STATUS', status: 'complete' })
          }
        } catch {
          // Keep polling
        }
      }, 3000)
    } catch {
      dispatch({ type: 'SET_SCRAPE_STATUS', status: 'failed' })
      dispatch({ type: 'SET_ERROR', error: 'Failed to analyze website. You can enter details manually.' })
    }
  }, [mode])

  const generateGreeting = useCallback((name: string, firmName: string) => {
    const agentName = name || 'Alex'
    const firm = firmName || 'our office'
    return `Good {{timeOfDay}}, thank you for calling ${firm}. This is ${agentName}, the firm's AI assistant. How can I help you today?`
  }, [])

  const playVoicePreview = useCallback((previewUrl: string) => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    if (!previewUrl) return
    const audio = new Audio(previewUrl)
    audioRef.current = audio
    audio.play().catch(() => {})
    audio.addEventListener('ended', () => { audioRef.current = null })
  }, [])

  const stopVoicePreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
  }, [])

  const createAgents = useCallback(async (): Promise<boolean> => {
    dispatch({ type: 'SET_CREATING', creating: true })
    dispatch({ type: 'SET_ERROR', error: null })

    try {
      // Step 1: Save voice config
      await api.put('/api/voice/config', {
        voiceId: state.selectedVoiceId,
        tonePreset: state.tonePreset,
        greetingName: state.businessName,
        practiceAreas: state.practiceAreas,
        agentName: state.agentName,
        wizardCompleted: true,
        wizardResponses: state.wizardResponses,
        // Operational fields
        firmTimezone: state.firmTimezone,
        businessHours: state.businessHours,
        officePhone: state.officePhone,
        primaryLanguage: state.primaryLanguage,
        additionalLanguages: state.additionalLanguages,
        afterHoursMessage: state.afterHoursMessage,
        bookingUrl: state.bookingUrl,
      })

      // Step 2: Create paired agents.
      // Provisioning two Telnyx AI Assistants + baking their prompts +
      // optionally indexing knowledge items routinely takes 15-30s — the
      // global 10s default times out mid-request. 90s gives plenty of
      // headroom; if it actually exceeds that, something's stuck.
      const createRes = await api.post(
        '/api/voice/assistants/create-pair',
        {
          greeting: state.greeting || generateGreeting(state.agentName, state.businessName),
          knowledgeItems: state.knowledgeItems.length > 0 ? state.knowledgeItems : undefined,
        },
        { timeout: 90_000 }
      )

      // Step 3: Assign phone number if selected
      const inboundId = createRes.data?.inbound?.id
      const outboundId = createRes.data?.outbound?.id
      if (state.selectedPhoneId && state.selectedPhoneId !== 'none') {
        if (inboundId) {
          await api.post(`/api/voice/numbers/${state.selectedPhoneId}/assign-assistant`, {
            assistantId: inboundId,
          }).catch(err => console.warn('Inbound phone assignment failed:', err))
        }
        if (outboundId) {
          await api.post(`/api/voice/numbers/${state.selectedPhoneId}/assign-outbound-agent`, {
            assistantId: outboundId,
          }).catch(err => console.warn('Outbound phone assignment failed:', err))
        }
      }

      return true
    } catch (err: any) {
      // Backend returns { success: false, message: '...' } from createError —
      // check both `.message` and `.error` since older handlers use the latter.
      const serverMsg = err.response?.data?.message || err.response?.data?.error
      dispatch({ type: 'SET_ERROR', error: serverMsg || err.message || 'Failed to create agents.' })
      return false
    } finally {
      dispatch({ type: 'SET_CREATING', creating: false })
    }
  }, [state, generateGreeting])

  return {
    state,
    dispatch,
    startScrape,
    generateGreeting,
    playVoicePreview,
    stopVoicePreview,
    createAgents,
  }
}
